<template>
    <BaseCard v-bind="$attrs" class="flex justify-between gap-4 text-ink">
        <div class="flex flex-col md:flex-row gap-y-2 gap-x-4 justify-center md:justify-start md:items-center grow">
            <p class="body-small font-semibold">{{ t("components.rolePlay.preview.afterYouGet") }}</p>
            <div v-for="{ iconClass, content } in previewContent" :key="content" class="flex gap-2 items-center">
                <BaseAvatar size="xs" aria-hidden="true">
                    <i :class="['bi text-[15px]/none', iconClass]" aria-hidden="true"></i>
                </BaseAvatar>
                <p class="body-small font-normal">{{ content }}</p>
            </div>
        </div>
        <BaseButton theme="neutral" size="sm" icon-only :aria-label="t('components.rolePlay.preview.previewReport')" class="self-start" @click="isPreviewSummaryOpen = true">
            <i class="bi bi-arrows-angle-expand icon-semibold text-sm leading-none" aria-hidden="true"></i>
        </BaseButton>
    </BaseCard>
    <!-- Preview the same report composition as the standalone page, including its own report heading. -->
    <BaseDialog v-model:open="isPreviewSummaryOpen" :title="t('components.rolePlay.preview.sampleRecapTitle')" layout="custom">
        <div class="md:w-[calc(100dvw-80px)] max-w-250 p-6 pt-0 relative">
            <OverallSummary
                :title="t('components.rolePlay.preview.sampleTitle')"
                :subtitle="t('components.rolePlay.preview.sampleSubtitle')"
                hide-cta
                :overall-summary="previewSummary"
                :summary-state="summaryState"
                :strengths="strengths"
                :weaknesses="weaknesses"
                :skills-breakdown="skillsBreakdown"
                :role-play-messages="rolePlayMessages" />
        </div>
    </BaseDialog>
</template>

<script setup>
import { ACTION } from "~vue/chatActions";
import BaseAvatar from "~vue/components/BaseAvatar.vue";
import BaseButton from "~vue/components/BaseButton.vue";
import BaseCard from "~vue/components/BaseCard.vue";
import { STATE } from "~vue/pages/RolePlaySummary.vue";
import OverallSummary from "~vue/RolePlaySummary/OverallSummary.vue";
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";

import BaseDialog from "./BaseDialog.vue";

const { t } = useI18n();

const isPreviewSummaryOpen = ref(false);

const previewContent = computed(() => [
    { iconClass: "bi-journal-text", content: t("components.rolePlay.preview.performanceBreakdown") },
    { iconClass: "bi-lightbulb", content: t("components.rolePlay.preview.personalizedInsights") },
    { iconClass: "bi-signpost-split", content: t("components.rolePlay.preview.actionableTips") },
]);

const previewSummary = computed(() => t("components.rolePlay.preview.sample.summary"));
const strengths = computed(() => [
    t("components.rolePlay.preview.sample.strength1"),
    t("components.rolePlay.preview.sample.strength2"),
    t("components.rolePlay.preview.sample.strength3"),
]);
const weaknesses = computed(() => [t("components.rolePlay.preview.sample.weakness1"), t("components.rolePlay.preview.sample.weakness2")]);
const skillsBreakdown = computed(() => [
    {
        name: t("components.rolePlay.preview.sample.communicationName"),
        score: 4,
        rationale: t("components.rolePlay.preview.sample.communicationRationale"),
        how_to_improve: t("components.rolePlay.preview.sample.communicationImprove"),
        state: {
            name: STATE.LOADED,
            rationale: STATE.LOADED,
            how_to_improve: STATE.LOADED,
        },
    },
    {
        name: t("components.rolePlay.preview.sample.emotionalName"),
        score: 4,
        rationale: t("components.rolePlay.preview.sample.emotionalRationale"),
        how_to_improve: t("components.rolePlay.preview.sample.emotionalImprove"),
        state: {
            name: STATE.LOADED,
            rationale: STATE.LOADED,
            how_to_improve: STATE.LOADED,
        },
    },
    {
        name: t("components.rolePlay.preview.sample.conflictName"),
        score: 5,
        rationale: t("components.rolePlay.preview.sample.conflictRationale"),
        how_to_improve: t("components.rolePlay.preview.sample.conflictImprove"),
        state: {
            name: STATE.LOADED,
            rationale: STATE.LOADED,
            how_to_improve: STATE.LOADED,
        },
    },
    {
        name: t("components.rolePlay.preview.sample.interpersonalName"),
        score: 4,
        rationale: t("components.rolePlay.preview.sample.interpersonalRationale"),
        how_to_improve: t("components.rolePlay.preview.sample.interpersonalImprove"),
        state: {
            name: STATE.LOADED,
            rationale: STATE.LOADED,
            how_to_improve: STATE.LOADED,
        },
    },
    {
        name: t("components.rolePlay.preview.sample.outcomeName"),
        score: 3,
        rationale: t("components.rolePlay.preview.sample.outcomeRationale"),
        how_to_improve: t("components.rolePlay.preview.sample.outcomeImprove"),
        state: {
            name: STATE.LOADED,
            rationale: STATE.LOADED,
            how_to_improve: STATE.LOADED,
        },
    },
    {
        name: t("components.rolePlay.preview.sample.overallName"),
        score: 4,
        rationale: t("components.rolePlay.preview.sample.overallRationale"),
        how_to_improve: t("components.rolePlay.preview.sample.overallImprove"),
        state: {
            name: STATE.LOADED,
            rationale: STATE.LOADED,
            how_to_improve: STATE.LOADED,
        },
    },
]);

const rolePlayMessages = computed(() => [
    {
        role: "assistant",
        lines: [
            {
                type: "md",
                content: t("components.rolePlay.preview.sample.a1"),
            },
        ],
    },
    {
        role: "user",
        lines: [
            { type: "md", content: t("components.rolePlay.preview.sample.u1") },
            {
                action_name: ACTION.ROLE_PLAY_MONITOR,
                action_params: {
                    sentiment: "praise",
                    feedback: t("components.rolePlay.preview.sample.u1Feedback"),
                },
            },
        ],
    },
    {
        role: "assistant",
        lines: [
            {
                type: "md",
                content: t("components.rolePlay.preview.sample.a2"),
            },
        ],
    },
    {
        role: "user",
        lines: [
            {
                type: "md",
                content: t("components.rolePlay.preview.sample.u2"),
            },
            {
                action_name: ACTION.ROLE_PLAY_MONITOR,
                action_params: {
                    sentiment: "suggestion",
                    feedback: t("components.rolePlay.preview.sample.u2Feedback"),
                },
            },
        ],
    },
    {
        role: "assistant",
        lines: [
            {
                type: "md",
                content: t("components.rolePlay.preview.sample.a3"),
            },
        ],
    },
    {
        role: "user",
        lines: [
            { type: "md", content: t("components.rolePlay.preview.sample.u3") },
            {
                action_name: ACTION.ROLE_PLAY_MONITOR,
                action_params: {
                    sentiment: "praise",
                    feedback: t("components.rolePlay.preview.sample.u3Feedback"),
                },
            },
        ],
    },
    {
        role: "assistant",
        lines: [
            {
                type: "md",
                content: t("components.rolePlay.preview.sample.a4"),
            },
        ],
    },
    {
        role: "user",
        lines: [{ type: "md", content: t("components.rolePlay.preview.sample.u4") }],
    },
    {
        role: "assistant",
        lines: [
            {
                type: "md",
                content: t("components.rolePlay.preview.sample.a5"),
            },
        ],
    },
    {
        role: "user",
        lines: [
            { type: "md", content: t("components.rolePlay.preview.sample.u5") },
            {
                action_name: ACTION.ROLE_PLAY_MONITOR,
                action_params: {
                    sentiment: "praise",
                    feedback: t("components.rolePlay.preview.sample.u5Feedback"),
                },
            },
        ],
    },
    {
        role: "assistant",
        lines: [
            {
                type: "md",
                content: t("components.rolePlay.preview.sample.a6"),
            },
        ],
    },
    {
        role: "user",
        lines: [{ type: "md", content: t("components.rolePlay.preview.sample.u6") }],
    },
    {
        role: "assistant",
        lines: [
            {
                type: "md",
                content: t("components.rolePlay.preview.sample.a7"),
            },
        ],
    },
    {
        role: "user",
        lines: [
            {
                type: "md",
                content: t("components.rolePlay.preview.sample.u7"),
            },
            {
                action_name: ACTION.ROLE_PLAY_MONITOR,
                action_params: {
                    sentiment: "praise",
                    feedback: t("components.rolePlay.preview.sample.u7Feedback"),
                },
            },
        ],
    },
    {
        role: "assistant",
        lines: [
            {
                type: "md",
                content: t("components.rolePlay.preview.sample.a8"),
            },
        ],
    },
    {
        role: "user",
        lines: [
            { type: "md", content: t("components.rolePlay.preview.sample.u8") },
            {
                action_name: ACTION.ROLE_PLAY_MONITOR,
                action_params: {
                    sentiment: "suggestion",
                    feedback: t("components.rolePlay.preview.sample.u8Feedback"),
                },
            },
        ],
    },
    {
        role: "assistant",
        lines: [
            {
                type: "md",
                content: t("components.rolePlay.preview.sample.a9"),
            },
        ],
    },
    {
        role: "user",
        lines: [{ type: "md", content: t("components.rolePlay.preview.sample.u9") }],
    },
    {
        role: "assistant",
        lines: [
            {
                type: "md",
                content: t("components.rolePlay.preview.sample.a10"),
            },
        ],
    },
    {
        role: "user",
        lines: [{ type: "md", content: t("components.rolePlay.preview.sample.u10") }],
    },
    {
        role: "assistant",
        lines: [
            {
                type: "md",
                content: t("components.rolePlay.preview.sample.a11"),
            },
        ],
    },
]);
const summaryState = {
    overallSummary: STATE.LOADED,
    strengths: STATE.LOADED,
    weaknesses: STATE.LOADED,
};
</script>
